import { useStoreContact } from "@/context/StoreContactContext";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { BRAND } from "@/lib/data";

export default function Privacy() {
  const contact = useStoreContact();
  return (
    <LegalLayout title="Privacy Policy" updated="August 2026">
      <p className="text-lg text-foreground leading-relaxed" data-testid="privacy-intro">
        At {BRAND.name}, we respect your privacy. The information you share with
        us is used only by us to serve you better — <strong className="text-foreground">we never sell or share your
        personal data with anyone</strong>. This policy explains what we collect, why, and how we protect it.
      </p>

      <LegalSection n="01" title="Information We Collect">
        <p>We only collect the information needed to serve you:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li><strong className="text-foreground">Contact details</strong> — your name, email, and phone number when you fill our contact form or place a home-delivery order.</li>
          <li><strong className="text-foreground">Delivery information</strong> — your address and preferred delivery time for doorstep grocery drops.</li>
          <li><strong className="text-foreground">Your messages</strong> — any enquiry, feedback, or order list you send us.</li>
        </ul>
      </LegalSection>

      <LegalSection n="02" title="How We Use Your Information">
        <p>Your data is used strictly to run our neighbourhood store, including:</p>
        <ul className="list-disc pl-5 space-y-2">
          <li>Fulfilling your grocery orders and arranging home delivery.</li>
          <li>Responding to your enquiries, feedback, and support requests.</li>
          <li>Informing you about offers or updates <em>only if you have asked us to</em>.</li>
          <li>Improving the products we stock and the service we provide.</li>
        </ul>
      </LegalSection>

      <LegalSection n="03" title="We Do Not Share Your Data">
        <p>
          This is important to us: <strong className="text-foreground">your personal information is for {BRAND.name}'s own use only</strong>.
          We do not sell, rent, trade, or share your data with any third party, advertiser, or outside company.
          The only exception is if we are legally required to do so by a valid order from a government authority or court of law.
        </p>
      </LegalSection>

      <LegalSection n="04" title="Data Security">
        <p>
          We take reasonable steps to keep your information safe and store it securely. Access is limited to our
          store staff who need it to complete your order or answer your query. While no system is perfectly secure,
          we work to protect your data against loss, misuse, or unauthorised access.
        </p>
      </LegalSection>

      <LegalSection n="05" title="Cookies & Website Data">
        <p>
          Our website may use basic cookies and standard analytics to keep the site working smoothly and to
          understand how visitors use it. These do not identify you personally and are not used for selling ads.
        </p>
      </LegalSection>

      <LegalSection n="06" title="Your Rights">
        <p>
          You may ask us at any time to view, correct, or delete the personal information we hold about you.
          Just reach out using the contact details below and we will act on your request promptly.
        </p>
      </LegalSection>

      <LegalSection n="07" title="Children's Privacy">
        <p>
          Our services are meant for general shoppers. We do not knowingly collect personal information from
          children. If you believe a child has shared data with us, please contact us and we will remove it.
        </p>
      </LegalSection>

      <LegalSection n="08" title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. Any changes will be posted on this page with a
          revised "last updated" date. Please review it occasionally.
        </p>
      </LegalSection>

      <LegalSection n="09" title="Contact Us">
        <p>If you have any questions about your privacy or this policy, contact us:</p>
        <ul className="space-y-1">
          <li><strong className="text-foreground">{BRAND.name}</strong></li>
          <li>{BRAND.address}</li>
          {contact.phone && <li>Phone: <a href={contact.phoneHref} className="text-accent hover:underline">{contact.phone}</a></li>}
          {contact.email && <li>Email: <a href={`mailto:${contact.email}`} className="text-accent hover:underline">{contact.email}</a></li>}
        </ul>
      </LegalSection>
    </LegalLayout>
  );
}
